import { Box, Typography } from '@mui/material';
import { TournamentBracketMatchGameStatus } from '@okletsplay/core';
import { BracketSeedMatchGameEntity } from 'app/interfaces/entity';
import { uniqBy } from 'lodash';
import { BracketSeedStatus } from 'modules/bracket/components/BracketSeedStatus';
import { PlayoffGameChip } from 'modules/bracket/components/PlayoffGameChip';
import { useEditMatchGames } from 'modules/bracket/hooks';
import { TournamentMatchResultsFormFields } from 'modules/bracket/interfaces';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import React, { FC, useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { ErrorMessage } from 'shared/components/Form/ErrorMessage';
import { SelectInput } from 'shared/components/Form/SelectInput';
import { ValueOrDefaultFormat } from 'shared/components/Format/ValueOrDefaultFormat';
import { useStyles } from './ManageTournamentMatchResultsForm.styles';

interface ManageTournamentMatchResultsFormProps {
  matchGames: BracketSeedMatchGameEntity[];
}

const MATCH_RESULT_OPTIONS = [
  { label: '0', value: 0 },
  { label: '1', value: 1 },
];

export const ManageTournamentMatchResultsForm: FC<ManageTournamentMatchResultsFormProps> = ({
  matchGames,
}) => {
  const { classes } = useStyles();
  const tournamentStatus = useSelector(currentTournamentSelector.tournament)?.status;
  const isDrawAllowed = useSelector(currentTournamentSelector.isDrawAllowed);
  const teamsNames = useSelector(currentTournamentSelector.teamsNames);
  const {
    reset,
    control,
    trigger,
    getValues,
    formState: { errors },
  } = useFormContext<TournamentMatchResultsFormFields>();

  const { editableIndexes: editableResultIndexes } = useEditMatchGames(matchGames);

  const formValue: TournamentMatchResultsFormFields = useMemo(() => {
    const results = matchGames.map((matchGame, index) => ({
      matchGameId: matchGame.id,
      status: matchGame.status as TournamentBracketMatchGameStatus,
      results: matchGame.matchGameResults.map((gameResult) => ({
        matchGameIndex: index,
        status: matchGame.status as TournamentBracketMatchGameStatus,
        teamId: gameResult.opponentId as string,
        score: gameResult.score ?? 0,
      })),
    }));

    return { results };
  }, [matchGames]);

  const options = useMemo(() => {
    if (isDrawAllowed) {
      return [...MATCH_RESULT_OPTIONS, { label: '0.5', value: 0.5 }];
    }

    return MATCH_RESULT_OPTIONS;
  }, [isDrawAllowed]);

  useEffect(() => {
    reset(formValue);
  }, [reset, formValue, getValues]);

  return (
    <Box>
      {getValues().results.map((gameResult, gi) => {
        const scoreFieldErrors = uniqBy(
          (errors?.results?.[gi]?.results ?? []).map?.((error) => error?.score),
          'message'
        );

        return (
          <Box key={gi} className={classes.root}>
            <Box>
              <Box className={classes.card}>
                <Box>
                  <Typography pb={1}>{`Game ${gi + 1}`}</Typography>
                  <BracketSeedStatus
                    matchStatus={gameResult.status}
                    tournamentStatus={tournamentStatus}
                  />
                  {matchGames[gi]?.isPlayoffGame && (
                    <Box pl={1} display="inline">
                      <PlayoffGameChip />
                    </Box>
                  )}
                </Box>
                <Box className={classes.inputsContainer}>
                  {gameResult.results.map((result, ri) => {
                    return (
                      <Box key={ri} className={classes.inputContainer}>
                        <Typography className={classes.teamName}>
                          <ValueOrDefaultFormat value={teamsNames[result.teamId]} />
                        </Typography>
                        <Controller
                          control={control}
                          name={`results.${gi}.results.${ri}.score`}
                          render={({ field: { onChange, value, ...rest } }) => (
                            <Box ml={1}>
                              <SelectInput
                                disabled={!editableResultIndexes.includes(gi)}
                                onChange={(val) => {
                                  onChange(val);
                                  trigger();
                                }}
                                options={options}
                                value={value}
                                {...rest}
                              />
                            </Box>
                          )}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
            <Box display="flex" flexDirection="column">
              {scoreFieldErrors?.map?.((error, index) => (
                <ErrorMessage key={`error-${index}`} error={error?.message} />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
